import {
  IProvider,
  WithPrimaryKey,
  isObject,
  type IReffable,
  type Timestamp,
  type Timezone,
  type WithRef,
} from '@principle-theorem/shared';
import { isArray, isString } from 'lodash';
import { type Observable } from 'rxjs';
import { type IPracticeMigration } from './practice-migration';
import {
  FilterType,
  IMigrationDestination,
  ISourceEntityRecord,
  ISourceEntityRecordFilter,
} from './source-entity-record';
import { type IFileData } from './sql-seeding-translator';
import { ISQLTableData } from './sql-index-seeder';

export interface ISourceMetadata {
  name: string;
  version: string;
}

export interface ISource {
  metadata: ISourceMetadata;
  configuration: ISourceConfigurationItem[];
}

export interface ISourceConfigurationItem {
  name: string;
  description: string;
  value: string;
}

export interface ISourceHandler {
  migrationType: string;
  migrationVersion: string;
  source: ISource;
  entities: ISourceEntity[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entityHandlers: ISourceEntityHandler<any>[];
  canHandle(source: ISource): boolean;
  addEntities(migration: WithRef<IPracticeMigration>): Promise<void>;
  sqlSeedingTranslators?: IProvider<IFileData, string[]>[];
  sqlIndexSeeders?: IProvider<ISQLTableData, string[]>[];
}

export enum SourceEntityStatus {
  Uninitialised = 'uninitialised',
  Failed = 'failed',
  Syncing = 'syncing',
  Synced = 'synced',
  Locked = 'locked',
}

export interface IExpectedSourceRecordSize {
  expectedSize: number;
  expectedSizeCalculatedAt: Timestamp;
}

export const SOURCE_ENTITY_STATUS_MAP: {
  [key in SourceEntityStatus]: string;
} = {
  [SourceEntityStatus.Uninitialised]: 'default',
  [SourceEntityStatus.Syncing]: 'accent',
  [SourceEntityStatus.Synced]: 'accent',
  [SourceEntityStatus.Locked]: 'primary',
  [SourceEntityStatus.Failed]: 'warn',
};

export type ISourceEntity = {
  uid: string;
  metadata: ISourceEntityMetadata;
  size?: number;
  invalidCount?: number;
  sizeLastCalculatedAt?: Timestamp;
  sizeCalculationCount?: number;
} & (
  | {
      status: SourceEntityStatus.Uninitialised | SourceEntityStatus.Syncing;
    }
  | {
      status: SourceEntityStatus.Failed;
      errorMessage: string;
      resumeData?: object;
    }
  | {
      status: SourceEntityStatus.Synced | SourceEntityStatus.Locked;
      lastSync: Timestamp;
    }
) &
  Partial<IExpectedSourceRecordSize>;

export function hasExpectRecordSize(
  entity: ISourceEntity
): entity is ISourceEntity & IExpectedSourceRecordSize {
  return (
    entity.expectedSize !== undefined &&
    entity.expectedSizeCalculatedAt !== undefined
  );
}

export enum SourceEntityMigrationType {
  Automatic = 'automatic',
  Manual = 'manual',
}

export interface ISourceEntityMetadata {
  label: string;
  description: string;
  idPrefix: string;
  migrationType: SourceEntityMigrationType;
}

export enum SourceEntityCollection {
  Records = 'sourceRecords',
}

export interface ISourceEntityHandler<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Source extends ISourceEntityTypes = ISourceEntityTypes<any, any, any>,
> {
  sourceEntity: ISourceEntity;
  sourceQuery: string | string[];

  /**
   * Whether to enable running the record sync with an offset in the UI.
   */
  allowOffsetJob?: boolean;

  /**
   * The number of records to load at a time.
   */
  defaultOffsetSize?: number;

  /**
   * Skip running until no more results are returned.
   */
  isSingleRun?: boolean;

  /**
   * Use keyset (seek) pagination instead of LIMIT/OFFSET. Prefer this for
   * large tables where OFFSET becomes slow, or where rows shift between
   * pages. `cursorColumn` is the SQL column used to seek (e.g.
   * `"a._record_id"`); the query template must contain `KEYSET_PLACEHOLDER`
   * and no `OFFSET_PLACEHOLDER`.
   */
  keysetPagination?: {
    cursorColumn: string;
  };

  /**
   * Mutable ref box populated after each `getFromSource$` run for keyset
   * entities. Holds the numeric id of the last row fetched in this window.
   * `publishSourceJob` reads this to return `nextCursor` to the outer loop.
   * Undefined for non-keyset entities.
   */
  keysetCursorRef?: { value?: number };

  /**
   * The source entities that this source entity needs to have run first.
   */
  requiredEntities?: Record<symbol, ISourceEntityHandler<Source>>;

  /**
   * A list of the destination entity metadata keys that use the source entity for their source record.
   */
  migrationDestinations?: string[];

  canHandle(source: ISourceEntity): boolean;
  getFromSource$(
    migration: WithRef<IPracticeMigration>,
    startOffset?: number,
    endOffset?: number
  ): Observable<Source['data'][] | ISourceSyncErrorData>;
  verifySource(data: unknown, logError: boolean): data is Source['data'];
  getSourceRecordId(data: Source['data']): string | number;
  getSourceLabel(record: Source['data']): string;
  getFilterData?(data: Source['data'], timezone: Timezone): Source['filters'];
  filterRecords?<K extends keyof Source['filters']>(
    migration: WithRef<IPracticeMigration>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filterKey: any,
    value: Source['filters'][K],
    filterFn?: (record: IGetRecordResponse<Source>) => boolean
  ): Promise<IGetRecordResponse<Source>[]>;
  translate(
    initialValue: Source['data'],
    timezone: Timezone
  ): Source['translations'];
  getEntity$(
    migration: IReffable<IPracticeMigration>
  ): Observable<ISourceEntity | undefined>;
  getMapResourceType(): string;
  getRecord(
    migration: IReffable<IPracticeMigration>,
    recordId: string | number
  ): Promise<IGetRecordResponse<Source> | undefined>;
  getRecords$(
    migration: WithRef<IPracticeMigration>,
    bufferSize: number,
    limit?: number,
    migrationStatus?: IMigrationDestination,
    filter?: ISourceEntityRecordFilter
  ): Observable<IGetRecordResponse<Source>[]>;
  getSourceEntity(
    migration: IReffable<IPracticeMigration>
  ): Promise<WithRef<ISourceEntity>>;
  combineRecordWithData(
    record: WithPrimaryKey<ISourceEntityRecord<Source>>
  ): IGetRecordResponse<Source>;
  getExpectedRecordSize(
    migration: IReffable<IPracticeMigration>
  ): Promise<IExpectedSourceRecordSize>;
}

export type SourceEntityTypeGuard<Data> = (data: unknown) => data is Data;

export interface IGetRecordResponse<Source extends ISourceEntityTypes> {
  record: WithPrimaryKey<ISourceEntityRecord<Source>>;
  data: Source['data'];
}

export interface ISourceSyncErrorData {
  resumeData: object;
  errorMessage: string;
}

export function isSourceSyncErrorData(
  data: ISourceSyncErrorData | unknown[]
): data is ISourceSyncErrorData {
  return isObject(data) && isString(data.errorMessage) && !isArray(data);
}

export interface ISourceEntityTypes<
  Data extends object = object,
  Translations = unknown,
  Filters extends FilterType = FilterType,
> {
  data: Data;
  translations: Translations;
  filters: Filters;
}
